/**
 * DEVBRAINS CHATBOT
 * ─────────────────────────────────────────────────────────────
 * Static chatbot with:
 *  - Floating bubble (bottom-right)
 *  - Category tabs
 *  - Live search
 *  - Default "popular questions" on open
 *  - Message-bubble chat history
 *  - Markdown-like answer rendering (bold, newlines, bullets)
 *  - Follow-up question chips
 *  - Contact Support fallback
 *  - Unread badge
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  CATEGORIES, FAQS, POPULAR_QUESTIONS, GREETINGS,
  searchFaqs, getFaqById,
} from '../data/chatbotData';
import './Chatbot.css';

/* ── Simple markdown-like renderer ─────────────────────────── */
function RenderAnswer({ text }) {
  // Split on newlines, handle **bold**, bullet lists
  const lines = text.split('\n');
  return (
    <div className="cb-answer-body">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="cb-spacer" />;
        // Bold: **text**
        const parts = line.split(/(\*\*[^*]+\*\*)/g).map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j}>{part.slice(2, -2)}</strong>;
          }
          return part;
        });
        // Numbered or bullet list lines
        if (/^(\d+\.|•|-)/.test(line.trim())) {
          return <div key={i} className="cb-list-item">{parts}</div>;
        }
        return <p key={i} className="cb-answer-p">{parts}</p>;
      })}
    </div>
  );
}

/* ── Single message bubble ──────────────────────────────────── */
function Message({ msg, onFollowUp }) {
  if (msg.type === 'user') {
    return (
      <div className="cb-msg cb-msg-user">
        <div className="cb-bubble cb-bubble-user">{msg.text}</div>
      </div>
    );
  }

  if (msg.type === 'bot') {
    return (
      <div className="cb-msg cb-msg-bot">
        <div className="cb-bot-av">🤖</div>
        <div className="cb-bot-content">
          <div className="cb-bubble cb-bubble-bot">
            <RenderAnswer text={msg.text} />
            {msg.link && (
              <Link to={msg.link.path} className="cb-answer-link" onClick={msg.onLinkClick}>
                {msg.link.label}
              </Link>
            )}
          </div>
          {msg.followUps?.length > 0 && (
            <div className="cb-followups">
              <span className="cb-followup-label">You might also ask:</span>
              <div className="cb-followup-chips">
                {msg.followUps.map(id => {
                  const faq = getFaqById(id);
                  return faq ? (
                    <button key={id} className="cb-chip" onClick={() => onFollowUp(faq)}>
                      {faq.question}
                    </button>
                  ) : null;
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  if (msg.type === 'no-result') {
    return (
      <div className="cb-msg cb-msg-bot">
        <div className="cb-bot-av">🤖</div>
        <div className="cb-bot-content">
          <div className="cb-bubble cb-bubble-bot cb-bubble-noresult">
            <div className="cb-noresult-icon">🔍</div>
            <p>I couldn't find an answer to <strong>"{msg.query}"</strong>.</p>
            <p>Try rewording your question, or reach out to our support team — they'll help within a few hours.</p>
            <Link to="/dashboard/mentee/support" className="cb-support-btn" onClick={msg.onLinkClick}>
              💬 Contact Support
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

/* ── Main Chatbot Component ─────────────────────────────────── */
export default function Chatbot() {
  const [open,           setOpen]         = useState(false);
  const [unread,         setUnread]       = useState(1);   // nudge on first load
  const [messages,       setMessages]     = useState([]);
  const [query,          setQuery]        = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [searchResults,  setSearchResults] = useState([]);
  const [showSearch,     setShowSearch]   = useState(false);
  const [isTyping,       setIsTyping]     = useState(false);
  const messagesEndRef  = useRef(null);
  const inputRef        = useRef(null);
  const searchDebounce  = useRef(null);

  /* ── Init greeting + popular questions ─────────────────────── */
  useEffect(() => {
    const greeting = GREETINGS[Math.floor(Math.random() * GREETINGS.length)];
    const popularFaqs = POPULAR_QUESTIONS.map(getFaqById).filter(Boolean);
    setMessages([
      { id: 0, type: 'greeting', text: greeting, popularFaqs },
    ]);
  }, []);

  /* ── Scroll to bottom on new message ─────────────────────────  */
  useEffect(() => {
    if (open) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
    }
  }, [messages, open]);

  /* ── Focus input when opened ──────────────────────────────── */
  useEffect(() => {
    if (open) {
      setUnread(0);
      setTimeout(() => inputRef.current?.focus(), 200);
    }
  }, [open]);

  /* ── Live search as user types ──────────────────────────────── */
  const handleQueryChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    setShowSearch(val.trim().length > 0);

    clearTimeout(searchDebounce.current);
    if (val.trim().length > 1) {
      searchDebounce.current = setTimeout(() => {
        const results = searchFaqs(val, activeCategory);
        setSearchResults(results.slice(0, 6));
      }, 200);
    } else {
      setSearchResults([]);
    }
  };

  /* ── Handle submit (Enter key or Send button) ──────────────── */
  const handleSubmit = useCallback(() => {
    const text = query.trim();
    if (!text) return;

    const msgId = Date.now();
    // Add user message
    setMessages(prev => [...prev, { id: msgId, type: 'user', text }]);
    setQuery('');
    setShowSearch(false);
    setSearchResults([]);

    // Simulate bot "typing"
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      const results = searchFaqs(text, activeCategory);
      if (results.length > 0) {
        const faq = results[0];
        setMessages(prev => [...prev, {
          id: msgId + 1,
          type: 'bot',
          text: faq.answer,
          followUps: faq.followUps,
          link: faq.link,
          onLinkClick: () => setOpen(false),
        }]);
      } else {
        setMessages(prev => [...prev, {
          id: msgId + 1,
          type: 'no-result',
          query: text,
          onLinkClick: () => setOpen(false),
        }]);
      }
    }, 600 + Math.random() * 400);
  }, [query, activeCategory]);

  /* ── Handle FAQ chip click (from search results or popular) ── */
  const handleFaqClick = useCallback((faq) => {
    setQuery('');
    setShowSearch(false);
    setSearchResults([]);

    const msgId = Date.now();
    setMessages(prev => [
      ...prev,
      { id: msgId,     type: 'user', text: faq.question },
    ]);
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
      setMessages(prev => [...prev, {
        id: msgId + 1,
        type: 'bot',
        text: faq.answer,
        followUps: faq.followUps,
        link: faq.link,
        onLinkClick: () => setOpen(false),
      }]);
    }, 500 + Math.random() * 300);
  }, []);

  /* ── Handle category tab switch ────────────────────────────── */
  const handleCategoryChange = (catId) => {
    setActiveCategory(catId);
    if (query.trim()) {
      const results = searchFaqs(query, catId);
      setSearchResults(results.slice(0, 6));
    }
  };

  /* ── Clear chat ────────────────────────────────────────────── */
  const handleClear = () => {
    const greeting = GREETINGS[Math.floor(Math.random() * GREETINGS.length)];
    const popularFaqs = POPULAR_QUESTIONS.map(getFaqById).filter(Boolean);
    setMessages([{ id: Date.now(), type: 'greeting', text: greeting, popularFaqs }]);
    setQuery('');
    setSearchResults([]);
    setShowSearch(false);
    setActiveCategory('all');
  };

  /* ── Category FAQ list now handled inline in greeting render ── */

  return (
    <>
      {/* ── Floating Bubble ─────────────────────────────────── */}
      <button
        className={`cb-bubble-btn ${open ? 'open' : ''}`}
        onClick={() => setOpen(o => !o)}
        aria-label="Open help chatbot"
      >
        {open ? (
          <span className="cb-bubble-icon">✕</span>
        ) : (
          <>
            <span className="cb-bubble-icon">💬</span>
            {unread > 0 && <span className="cb-unread-badge">{unread}</span>}
          </>
        )}
      </button>

      {/* ── Chat Window ─────────────────────────────────────── */}
      <div className={`cb-window ${open ? 'visible' : ''}`} role="dialog" aria-label="DevBrains Help Chat">

        {/* Header */}
        <div className="cb-header">
          <div className="cb-header-left">
            <div className="cb-header-av">🤖</div>
            <div>
              <div className="cb-header-name">DevBrains Assistant</div>
              <div className="cb-header-status">
                <span className="cb-online-dot" />
                Always online · Instant answers
              </div>
            </div>
          </div>
          <div className="cb-header-actions">
            <button className="cb-icon-btn" onClick={handleClear} title="Clear chat">🔄</button>
            <button className="cb-icon-btn" onClick={() => setOpen(false)} title="Close">✕</button>
          </div>
        </div>

        {/* Category tabs */}
        <div className="cb-tabs">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              className={`cb-tab ${activeCategory === cat.id ? 'active' : ''}`}
              onClick={() => handleCategoryChange(cat.id)}
            >
              {cat.emoji} {cat.id === 'all' ? 'All' : cat.label.split(' ').slice(1).join(' ')}
            </button>
          ))}
        </div>

        {/* Search input */}
        <div className="cb-search-wrap">
          <span className="cb-search-icon">🔍</span>
          <input
            ref={inputRef}
            className="cb-search-input"
            placeholder="Search or type a question..."
            value={query}
            onChange={handleQueryChange}
            onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
          />
          {query && (
            <button className="cb-clear-query" onClick={() => { setQuery(''); setShowSearch(false); setSearchResults([]); }}>✕</button>
          )}
        </div>

        {/* Search results dropdown */}
        {showSearch && (
          <div className="cb-search-results">
            {searchResults.length > 0 ? (
              <>
                <div className="cb-results-label">
                  {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
                </div>
                {searchResults.map(faq => (
                  <button key={faq.id} className="cb-result-item" onClick={() => handleFaqClick(faq)}>
                    <span className="cb-result-cat">{CATEGORIES.find(c => c.id === faq.category)?.emoji}</span>
                    <span className="cb-result-q">{faq.question}</span>
                    <span className="cb-result-arrow">›</span>
                  </button>
                ))}
                <button className="cb-result-send" onClick={handleSubmit}>
                  Send as message: "{query.length > 30 ? query.slice(0, 30) + '...' : query}" →
                </button>
              </>
            ) : (
              <div className="cb-no-search-result">
                <span>No results for "{query}"</span>
                <button className="cb-result-send" onClick={handleSubmit}>Send anyway →</button>
              </div>
            )}
          </div>
        )}

        {/* Message area */}
        <div className="cb-messages">
          {messages.map(msg => {
            /* Greeting card with popular questions */
            if (msg.type === 'greeting') {
              return (
                <div key={msg.id} className="cb-greeting">
                  <div className="cb-greeting-text">{msg.text}</div>

                  {/* Topic quick-pick buttons */}
                  {!showSearch && messages.length === 1 && (
                    <div className="cb-topics">
                      {CATEGORIES.filter(c => c.id !== 'all').map(cat => (
                        <button
                          key={cat.id}
                          className={`cb-topic-btn ${activeCategory === cat.id ? 'active' : ''}`}
                          onClick={() => handleCategoryChange(cat.id)}
                        >
                          {cat.emoji} {cat.label.replace(/^[^\s]+\s/, '')}
                        </button>
                      ))}
                    </div>
                  )}

                  {(!showSearch) && (
                    <div className="cb-popular">
                      <div className="cb-popular-label">
                        {activeCategory === 'all' ? '🔥 Popular questions' : `${CATEGORIES.find(c=>c.id===activeCategory)?.label} questions`}
                      </div>
                      {(activeCategory === 'all' ? msg.popularFaqs : FAQS.filter(f=>f.category===activeCategory).slice(0,6))?.map(faq => (
                        <button key={faq.id} className="cb-popular-item" onClick={() => handleFaqClick(faq)}>
                          <span className="cb-popular-emoji">{CATEGORIES.find(c => c.id === faq.category)?.emoji}</span>
                          <span>{faq.question}</span>
                          <span className="cb-popular-arrow">›</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }
            return <Message key={msg.id} msg={msg} onFollowUp={handleFaqClick} />;
          })}

          {/* Typing indicator */}
          {isTyping && (
            <div className="cb-msg cb-msg-bot">
              <div className="cb-bot-av">🤖</div>
              <div className="cb-bubble cb-bubble-bot cb-typing">
                <span /><span /><span />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Footer input area */}
        <div className="cb-footer">
          <input
            className="cb-footer-input"
            placeholder="Type your question..."
            value={query}
            onChange={handleQueryChange}
            onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
          />
          <button
            className={`cb-send-btn ${query.trim() ? 'active' : ''}`}
            onClick={handleSubmit}
            disabled={!query.trim()}
            title="Send"
          >
            ➤
          </button>
        </div>

        {/* Branding */}
        <div className="cb-brand">Powered by <strong>DevBrains</strong> · devbrains.in</div>
      </div>
    </>
  );
}
